import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import CreateTenderingContext, {
  TENDERING_CREATE_STEPS,
} from "../CreateTenderingContext";
import AppContext from "../../../../AppContext";
import {
  formatFileSize,
  formatNumber,
} from "../../../../helpers/common.helper";
import { Button } from "../../../../components";
import { ReactComponent as DownloadSvg } from "../../../../assets/icons/tendering/download.svg";
import { ReactComponent as DocumentSvg } from "../../../../assets/icons/tendering/document.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/tendering/info.svg";
import { ReactComponent as ArrowRightSvg } from "../../../../assets/workExchange/arrow-right-circle.svg";
import { ReactComponent as ArrowLeftSvg } from "../../../../assets/workExchange/arrow-left-circle.svg";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { format } from "date-fns";
import useTendering from "../../../../hooks/useTendering";
import { ObjectId } from "bson";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const assetsUri = import.meta.env.ASSETS_URI;

const TaxRequirements = {
  vat: "Vat 7%",
  tax: "Tax 3%",
  none: "None",
};

const ConstructionSummary = (props) => {
  const { user, designStyles } = useContext(AppContext);
  const { details, goBackward, setCurrentStep } = useContext(
    CreateTenderingContext
  );
  const { create, update, getSignedUrl } = useTendering();
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const { _id, info, images, files, type } = details;

  // * Get Design styles
  const {
    name,
    desc,
    startDate,
    endDate,
    typeOfWork,
    typeOfSpace,
    typeOfStyles,
    location,
    googleMap,
    duration,
    durationUnit,
    budget,
    bankGuarantee,
    taxRequirement,
    areaSize,
    areaUnit,
    retention,
  } = info;

  useEffect(() => {
    setCurrentStep(TENDERING_CREATE_STEPS.SUMMARY);
  }, []);

  const { cover, gallery, renderings, finalImages } = images;

  const { drawings, miscellaneous, perspectiveRenders } = files ?? {};

  const _renderInfoName = (name, infoIcon) => {
    return (
      <span className="tendering-detail__info__name">
        {name}
        {infoIcon === true && <InfoIcon width={18} />}
      </span>
    );
  };

  const _renderInfoValue = (value) => {
    return <span className="tendering-detail__info__value">{value}</span>;
  };

  const _getOwner = () => {
    // * Belongs to a company
    if (user.company) {
      return (
        <div className="tendering-detail__owner">
          <img
            className="tendering-detail__owner__avatar"
            src={`${assetsUri}/${user.company.logo}`}
          />
          <span className="tendering-detail__owner__name">
            {user.company.name}
          </span>
        </div>
      );
    }

    return (
      <div className="tendering-detail__owner">
        <img
          className="tendering-detail__owner__avatar"
          src={`${assetsUri}/${user?.avatar}`}
        />
        <span className="tendering-detail__owner__name">
          {user.firstname}
          {user.surname ? ` ${user.surname}` : ""}
        </span>
      </div>
    );
  };

  /**
   * Upload image and return object for saving
   * @returns
   */
  const _uploadImage = async (tenderingId, type, image) => {
    const _id = image._id ?? new ObjectId();

    // * Upload original
    const originalKey = `tenderings/${tenderingId}/images/${type}/${_id}/original`;

    // * Skip original if updating an image
    if (!image._id) {
      const originalSignedUrl = await getSignedUrl(
        originalKey,
        image.file.type
      );

      const uploadOriginalResponse = await fetch(originalSignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": image.file.type,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: 0,
        },
        body: image.file,
      });

      if (uploadOriginalResponse.status !== 200) {
        toast.error("Unable to upload cover");
        return;
      }
    }

    if (!image.blob) {
      return {
        _id: _id,
        original: originalKey,
        name: image.file.name,
        mimeType: image.file.type,
      };
    }

    // * Upload cropped if exist
    const croppedKey = `tenderings/${tenderingId}/images/${type}/${_id}/cropped`;
    const croppedSignedUrl = await getSignedUrl(croppedKey, image.blob?.type);

    const coverUploadResponse = await fetch(croppedSignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": image.blob?.type,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: 0,
      },
      body: image.blob,
    });

    if (coverUploadResponse.status !== 200) {
      toast.error("Unable to upload cover");
      return;
    }

    return {
      _id: _id,
      original: originalKey,
      name: image.file.name,
      mimeType: image.file.type,
      cropped: croppedKey,
      cropPosition: image.crop,
    };
  };

  /**
   * Upload image and return object for saving
   * @returns
   */
  const _uploadDocument = async (tenderingId, type, file) => {
    const _id = file._id ?? new ObjectId();

    // * Upload original
    const originalKey = `tenderings/${tenderingId}/documents/${type}/${_id}`;

    // * Skip original if updating an image
    if (!file._id) {
      const originalSignedUrl = await getSignedUrl(originalKey, file.file.type);

      const uploadOriginalResponse = await fetch(originalSignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.file.type,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: 0,
        },
        body: file.file,
      });

      if (uploadOriginalResponse.status !== 200) {
        toast.error("Unable to upload documents");
        return;
      }
    }

    return {
      _id: _id,
      key: originalKey,
      name: file.file.name,
      mimeType: file.file.type,
      size: file.file.size,
    };
  };

  const handlePublishClick = async () => {
    setSaving(true);

    const tenderingId = new ObjectId();

    // * Handle images
    const _cover = await _uploadImage(tenderingId, "cover", cover);
    let _gallery = [];
    let _renderings = [];

    for (const image of gallery) {
      const _image = await _uploadImage(tenderingId, "gallery", image);
      _gallery.push(_image);
    }

    for (const image of renderings) {
      const _image = await _uploadImage(tenderingId, "renderings", image);
      _renderings.push(_image);
    }

    // * Handle documents
    let _drawings = [];
    let _miscellaneous = [];
    let _perspectiveRenders = [];

    for (const file of drawings) {
      const _doc = await _uploadDocument(tenderingId, "drawings", file);
      _drawings.push(_doc);
    }

    for (const file of miscellaneous) {
      const _doc = await _uploadDocument(tenderingId, "miscellaneous", file);
      _miscellaneous.push(_doc);
    }

    for (const file of perspectiveRenders) {
      const _doc = await _uploadDocument(
        tenderingId,
        "perspectiveRenders",
        file
      );
      _perspectiveRenders.push(_doc);
    }

    let body = {
      _id: tenderingId,
      name,
      desc,
      category: +type,
      period: {
        start: new Date(startDate),
        end: new Date(endDate),
      },
      work: typeOfWork,
      space: typeOfSpace,
      styles: typeOfStyles,
      location,
      googleMap,
      duration: {
        value: duration,
        unit: durationUnit,
      },
      area: {
        size: areaSize,
        unit: areaUnit,
      },
      budget,
      bankGuarantee,
      retention,
      taxRequirement,
      images: {
        cover: _cover,
        gallery: _gallery,
        renderings: _renderings,
      },
      documents: {
        drawings: _drawings,
        miscellaneous: _miscellaneous,
        perspectiveRenders: _perspectiveRenders,
      },
      status: 1,
    };

    if (user.company) {
      body.company = user.company;
    }

    const result = await create(body);

    setSaving(false);

    navigate(`/tenderings/${tenderingId}`);
  };

  const handleUpdateClick = async () => {
    setSaving(true);

    // * Handle images
    const _cover = await _uploadImage(_id, "cover", cover);
    let _gallery = [];
    let _renderings = [];

    for (const image of gallery) {
      const _image = await _uploadImage(_id, "gallery", image);
      _gallery.push(_image);
    }

    for (const image of renderings) {
      const _image = await _uploadImage(_id, "renderings", image);
      _renderings.push(_image);
    }

    // * Handle documents
    let _drawings = [];
    let _miscellaneous = [];
    let _perspectiveRenders = [];

    for (const file of drawings) {
      const _doc = await _uploadDocument(_id, "drawings", file);
      _drawings.push(_doc);
    }

    for (const file of miscellaneous) {
      const _doc = await _uploadDocument(_id, "miscellaneous", file);
      _miscellaneous.push(_doc);
    }

    for (const file of perspectiveRenders) {
      const _doc = await _uploadDocument(_id, "perspectiveRenders", file);
      _perspectiveRenders.push(_doc);
    }

    try {
      const result = await update(_id, {
        name,
        desc,
        period: {
          start: new Date(startDate),
          end: new Date(endDate),
        },
        work: typeOfWork,
        space: typeOfSpace,
        styles: typeOfStyles,
        location,
        googleMap,
        duration: {
          value: duration,
          unit: durationUnit,
        },
        area: {
          size: areaSize,
          unit: areaUnit,
        },
        budget,
        bankGuarantee,
        retention,
        taxRequirement,
        images: {
          cover: _cover,
          gallery: _gallery,
          renderings: _renderings,
        },
        documents: {
          drawings: _drawings,
          miscellaneous: _miscellaneous,
          perspectiveRenders: _perspectiveRenders,
        },
        createdBy: user._id,
      });

      navigate(`/tenderings/${_id}`);
    } catch (error) {
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tendering-detail" data-state="summary">
      <div className="tendering-detail__container">
        <h3 className="tendering-detail__title">{info.name}</h3>
        {_getOwner()}
        <div className="tendering-detail__dates">
          <span className="tendering-detail__dates__title">Tender Date</span>
          <span>
            {format(new Date(startDate), "dd MMM yyyy")}
            {" - "}
            {format(new Date(endDate), "dd MMM yyyy")}
          </span>
        </div>
        <img
          className="tendering-detail__cover"
          src={images.cover.croppedDataUrl ?? images.cover.dataUrl}
        />
        {images.gallery?.length > 0 && (
          <div className="tendering-detail__gallery">
            <Swiper
              className="tendering-detail__gallery__swiper"
              modules={[Navigation, Pagination]}
              spaceBetween={12}
              slidesPerView={3}
              slidesPerGroup={3}
              navigation={{
                enabled: true,
                enabled: true,
                prevEl: ".tendering-detail__gallery__controls__arrows--prev",
                nextEl: ".tendering-detail__gallery__controls__arrows--next",
              }}
              pagination={{
                clickable: true,
                el: ".tendering-detail__gallery__controls__pagination",
                bulletClass:
                  "tendering-detail__gallery__controls__pagination__bullet",
                bulletActiveClass:
                  "tendering-detail__gallery__controls__pagination__bullet--active",
                renderBullet: function (index, className) {
                  return '<span class="' + className + '">' + "</span>";
                },
              }}
              loop={true}
              breakpoints={{
                320: {
                  slidesPerView: 1,
                },
                768: {
                  slidesPerView: 2,
                },
                1200: {
                  slidesPerView: 3,
                },
              }}
            >
              {gallery.map((image, index) => {
                return (
                  <SwiperSlide className="tendering-detail__gallery__swiper__slide">
                    <img
                      className="tendering-detail__gallery__swiper__slide__image"
                      src={image.croppedDataUrl ?? image.dataUrl}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className="tendering-detail__gallery__controls">
              <ArrowLeftSvg
                className="tendering-detail__gallery__controls__arrows tendering-detail__gallery__controls__arrows--prev"
                width={32}
              />
              <div className="tendering-detail__gallery__controls__pagination" />
              <ArrowRightSvg
                className="tendering-detail__gallery__controls__arrows tendering-detail__gallery__controls__arrows--next"
                width={32}
              />
            </div>
          </div>
        )}
        <div className="tendering-detail__desc">
          <p className="tendering-detail__desc__text">{info.desc}</p>
        </div>
        <div className="tendering-detail__info">
          <h4 className="tendering-detail__info__title">Information</h4>
          {_renderInfoName("Project type")}
          {_renderInfoValue("Design")}
          {_renderInfoName("Type of work")}
          {_renderInfoValue(
            <div className="tendering-detail__info__tag--work">
              {designStyles.works.find((w) => w.value === typeOfWork)?.name}
            </div>
          )}
          {_renderInfoName("Type of space")}
          {_renderInfoValue(
            <div className="tendering-detail__info__tag--space">
              {designStyles.spaces.find((w) => w.value === typeOfSpace)?.name}
            </div>
          )}
          {_renderInfoName("Type of styles")}
          {_renderInfoValue(
            typeOfStyles.map((tos) => (
              <div className="tendering-detail__info__tag--space">
                {designStyles.styles.find((w) => w.value === tos)?.name}
              </div>
            ))
          )}
          {/* {_renderInfoName("Area")}
        {_renderInfoValue("-")} */}
          {_renderInfoName("Location")}
          {_renderInfoValue(location)}
          {_renderInfoName("Duration")}
          {_renderInfoValue(`${duration} ${durationUnit}`)}
          {_renderInfoName("Tax requirements")}
          {_renderInfoValue(TaxRequirements[taxRequirement])}
          {_renderInfoName("Budget")}
          {_renderInfoValue(formatNumber(budget, "THB"))}
          {bankGuarantee && (
            <>
              {_renderInfoName("Bank Guarantee")}
              {_renderInfoValue(`${bankGuarantee}%`)}
            </>
          )}
          {_renderInfoName("Security Deposit", true)}
          {_renderInfoValue(retention === true ? "Yes" : "No")}
        </div>
        <div className="tendering-detail__other">
          <div className="tendering-detail__other__header">
            <h4 className="tendering-detail__other__title">3D Renderings</h4>
            <div className="tendering-detail__other__gallery__controls">
              <ArrowLeftSvg
                className="tendering-detail__other__gallery__controls__arrows tendering-detail__other__gallery__controls__arrows--prev"
                width={32}
              />
              <ArrowRightSvg
                className="tendering-detail__other__gallery__controls__arrows tendering-detail__other__gallery__controls__arrows--next"
                width={32}
              />
            </div>
          </div>
          <div className="tendering-detail__other__gallery">
            <Swiper
              className="tendering-detail__other__gallery__swiper"
              modules={[Navigation, Pagination]}
              spaceBetween={0}
              slidesPerView={1}
              loop={true}
              navigation={{
                enabled: true,
                prevEl:
                  ".tendering-detail__other__gallery__controls__arrows--prev",
                nextEl:
                  ".tendering-detail__other__gallery__controls__arrows--next",
              }}
              pagination={{
                clickable: true,
                el: ".tendering-detail__other__gallery__controls__pagination",
                bulletClass:
                  "tendering-detail__other__gallery__controls__pagination__bullet",
                bulletActiveClass:
                  "tendering-detail__other__gallery__controls__pagination__bullet--active",
                renderBullet: function (index, className) {
                  return '<span class="' + className + '">' + "</span>";
                },
              }}
            >
              {(renderings ?? []).map((image, index) => {
                return (
                  <SwiperSlide className="tendering-detail__other__gallery__swiper__slide">
                    <img
                      className="tendering-detail__other__gallery__swiper__slide__image"
                      src={image.croppedDataUrl ?? image.dataUrl}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className="tendering-detail__other__gallery__controls">
              <div className="tendering-detail__other__gallery__controls__pagination" />
            </div>
          </div>
        </div>
        {(drawings.length > 0 ||
          miscellaneous.length > 0 ||
          perspectiveRenders.length > 0) && (
          <div className="tendering-detail__documents">
            <h4 className="tendering-detail__documents__title">Documents</h4>
            {drawings.length > 0 && (
              <>
                <div className="tendering-detail__documents__section">
                  <span className="tendering-detail__documents__section__title">
                    Drawings
                  </span>
                  <div className="tendering-detail__documents__section__files">
                    {drawings.map(({ file }) => (
                      <div className="tendering-detail__documents__section__files__file">
                        <div className="tendering-detail__documents__section__files__file__icon">
                          <DocumentSvg width={32} />
                        </div>
                        <span className="tendering-detail__documents__section__files__file__name">
                          {file.name}
                        </span>
                        <span className="tendering-detail__documents__section__files__file__size">
                          {formatFileSize(file.size)}
                        </span>
                        <div className="tendering-detail__documents__section__files__file__download">
                          <DownloadSvg width={32} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="tendering-detail__documents__separator" />
              </>
            )}
            {perspectiveRenders.length > 0 && (
              <>
                <div className="tendering-detail__documents__section">
                  <span className="tendering-detail__documents__section__title">
                    Perspective Renders
                  </span>
                  <div className="tendering-detail__documents__section__files">
                    {perspectiveRenders.map(({ file }) => (
                      <div className="tendering-detail__documents__section__files__file">
                        <div className="tendering-detail__documents__section__files__file__icon">
                          <DocumentSvg width={32} />
                        </div>
                        <span className="tendering-detail__documents__section__files__file__name">
                          {file.name}
                        </span>
                        <span className="tendering-detail__documents__section__files__file__size">
                          {formatFileSize(file.size)}
                        </span>
                        <div className="tendering-detail__documents__section__files__file__download">
                          <DownloadSvg width={32} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="tendering-detail__documents__separator" />
              </>
            )}
            {miscellaneous.length > 0 && (
              <>
                <div className="tendering-detail__documents__section">
                  <span className="tendering-detail__documents__section__title">
                    Miscellaneous
                  </span>
                  <div className="tendering-detail__documents__section__files">
                    {miscellaneous.map(({ file }) => (
                      <div className="tendering-detail__documents__section__files__file">
                        <div className="tendering-detail__documents__section__files__file__icon">
                          <DocumentSvg width={32} />
                        </div>
                        <span className="tendering-detail__documents__section__files__file__name">
                          {file.name}
                        </span>
                        <span className="tendering-detail__documents__section__files__file__size">
                          {formatFileSize(file.size)}
                        </span>
                        <div className="tendering-detail__documents__section__files__file__download">
                          <DownloadSvg width={32} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <div className="tendering-detail__footer">
          {saving && <>Saving</>}
          {!saving && (
            <>
              <Button
                label="Back"
                variant={"secondary"}
                onClick={() => goBackward()}
              />
              {!_id && (
                <Button
                  label="Publish"
                  type="submit"
                  onClick={handlePublishClick}
                />
              )}
              {_id && (
                <Button
                  label="Save"
                  type="submit"
                  onClick={handleUpdateClick}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

ConstructionSummary.propTypes = {};

export default ConstructionSummary;
